import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Calendar, Clock, Handshake } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const VENUE_PHOTOS = [
  { src: "/assets/cafe-storefront.jpg", alt: "Poolside Cooking Café exterior" },
  { src: "/assets/hero-cafe.jpg", alt: "Inside the Poolside Cooking Café" },
  { src: "/assets/cafe-event.jpg", alt: "Event setup at the Café" },
  { src: "/assets/guest-9.jpg", alt: "Celebrity brunch at the Café" },
  { src: "/assets/guest-8.jpg", alt: "Guests at a Poolside Cooking Café event" },
];

function EventListing({ ev, index, featured = false }) {
  return (
    <Reveal delay={index * 0.08}>
      <div
        className={`grid ${featured ? "lg:grid-cols-[42%_58%]" : "sm:grid-cols-[38%_62%]"} bg-white rounded-2xl overflow-hidden shadow-[0_16px_50px_rgba(2,20,30,0.1)] border ${featured ? "border-pool/40" : "border-slate-100"}`}
        data-testid={`event-card-${ev.id}`}
      >
        <div className={`relative ${featured ? "aspect-[4/5] lg:aspect-auto" : "aspect-[4/5] sm:aspect-auto"} sm:h-full overflow-hidden bg-ink`}>
          <img src={imgSrc(ev.image)} alt={ev.title} loading="lazy" className="img-zoom absolute inset-0 h-full w-full object-cover" />
          {featured && (
            <span className="absolute top-4 left-4 bg-pool text-white text-[10px] tracking-[0.22em] uppercase font-bold px-3.5 py-1.5 rounded-full shadow-lg">
              Featured Event
            </span>
          )}
        </div>
        <div className={`p-7 ${featured ? "sm:p-12" : "sm:p-10"} flex flex-col justify-center`}>
          {ev.kind && (
            <p className="text-[10px] tracking-[0.28em] uppercase font-bold text-pool mb-3" data-testid={`event-kind-${ev.id}`}>{ev.kind}</p>
          )}
          <h3 className={`font-display font-black ${featured ? "text-3xl sm:text-5xl" : "text-2xl sm:text-4xl"} text-ink leading-tight`} data-testid={`event-title-${ev.id}`}>
            {ev.title}
          </h3>
          <div className="mt-5 space-y-2.5 text-sm sm:text-base text-slate-600">
            {ev.date && (
              <p className="flex items-center gap-3" data-testid={`event-date-${ev.id}`}>
                <Calendar className="h-4 w-4 text-pool shrink-0" /> {ev.date}
              </p>
            )}
            {ev.time && (
              <p className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-pool shrink-0" /> {ev.time}
              </p>
            )}
          </div>
          {ev.description && <p className="mt-5 text-slate-600 text-sm sm:text-base leading-relaxed max-w-md">{ev.description}</p>}
          <div className="mt-7 flex flex-wrap items-center gap-4">
            {ev.link && (
              <>
                <a
                  href={ev.link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`event-tickets-${ev.id}`}
                  className="inline-flex items-center gap-3 bg-pool hover:bg-pool-dark text-white font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors"
                >
                  Get Tickets <ArrowUpRight className="h-4 w-4" />
                </a>
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">via Eventbrite</span>
              </>
            )}
            {featured && (
              <Link
                to="/contact?type=Partnerships%20%26%20Sponsorships"
                data-testid="event-sponsor-cta"
                className="inline-flex items-center gap-3 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors"
              >
                Sponsor This Event <Handshake className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </Reveal>
  );
}

function PastEventCard({ ev, index }) {
  return (
    <Reveal delay={index * 0.06} className="h-full">
      <div
        className="group h-full bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-[0_8px_28px_rgba(2,20,30,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(2,120,150,0.14)]"
        data-testid={`past-event-card-${ev.id}`}
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-ink">
          <img src={imgSrc(ev.image)} alt={ev.title} loading="lazy" className="img-zoom absolute inset-0 h-full w-full object-cover" />
        </div>
        <div className="p-5 sm:p-6">
          <h3 className="font-display font-black text-base sm:text-lg text-ink leading-tight uppercase tracking-wide" data-testid={`past-event-title-${ev.id}`}>
            {ev.title}
          </h3>
          <p className="mt-1.5 text-[10px] tracking-[0.24em] uppercase font-bold text-pool" data-testid={`past-event-meta-${ev.id}`}>
            {ev.kind}
          </p>
          {ev.date && <p className="mt-1 text-xs text-slate-500">{ev.date.replace(/^[^,]+,\s*/, "")}</p>}
          {ev.guest_link && (
            <Link
              to={ev.guest_link}
              data-testid={`past-event-guest-${ev.id}`}
              className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-bold text-pool-deep hover:text-pool transition-colors"
            >
              View Guest <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          )}
        </div>
      </div>
    </Reveal>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const location = useLocation();

  useEffect(() => {
    api.get("/content/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.hash === "#book-your-event") {
      const el = document.querySelector('[data-testid="book-your-event"]');
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [location.hash, events.length]);

  const eventTime = (ev) => {
    if (ev.event_date) {
      const t = Date.parse(ev.event_date + "T23:59:59");
      if (!isNaN(t)) return t;
    }
    if (ev.date) {
      const t = Date.parse(ev.date.replace(/^[^,]+,\s*/, ""));
      if (!isNaN(t)) return t;
    }
    return null;
  };
  const now = Date.now();
  const upcoming = events.filter((ev) => eventTime(ev) === null || eventTime(ev) >= now);
  const past = events.filter((ev) => eventTime(ev) !== null && eventTime(ev) < now);
  const [featured, ...rest] = upcoming;

  return (
    <div data-testid="events-page">
      {/* HERO — bright water brand moment */}
      <section className="relative overflow-hidden" data-testid="events-hero">
        <img src="/assets/shop-hero.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/75 to-white/35 pointer-events-none" aria-hidden="true" />
        <div className="relative container-site grid lg:grid-cols-[58%_42%] gap-10 items-center py-14 sm:py-16 lg:py-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="label-eyebrow mb-4">Poolside Cooking Events</p>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.0] text-ink" data-testid="events-hero-title">
              Events &amp; <span className="text-pool">Experiences.</span>
            </h1>
            <p className="mt-5 max-w-xl text-sm sm:text-base leading-relaxed text-ink/70" data-testid="events-hero-sub">
              Celebrity brunches, special gatherings and unforgettable moments — live at the Poolside Cooking Café.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="relative hidden lg:block"
          >
            <div className="bg-white p-2.5 rounded-[1.6rem] shadow-[0_24px_60px_rgba(2,60,80,0.16)]" data-testid="events-hero-image">
              <div className="overflow-hidden rounded-[1.2rem] aspect-[4/3]">
                <img src="/assets/cafe-event.jpg" alt="Event setup at the Poolside Cooking Café" loading="lazy" className="img-zoom h-full w-full object-cover" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 1 — POOLSIDE COOKING EVENTS */}
      <section className="py-16 sm:py-20 bg-cream" data-testid="events-listing">
        <div className="container-site">
          <Reveal className="mb-12">
            <p className="label-eyebrow mb-4">Save the Date</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-[2.6rem] tracking-tight text-ink" data-testid="events-heading">
              Upcoming <span className="text-pool">Events.</span>
            </h2>
            <p className="mt-4 text-slate-600 max-w-xl text-sm sm:text-base leading-relaxed">
              Official Poolside Cooking events — celebrity brunches and live experiences hosted by the show.
            </p>
          </Reveal>
          <div className="grid gap-8" data-testid="events-grid">
            {featured && <EventListing ev={featured} index={0} featured />}
            {rest.map((ev, i) => (
              <EventListing key={ev.id} ev={ev} index={i + 1} />
            ))}
          </div>
          {upcoming.length === 0 && (
            <p className="text-slate-500 text-sm py-16 text-center" data-testid="events-empty">
              New event announcements are on the way — check back soon.
            </p>
          )}
        </div>
      </section>

      {/* 2 — PAST EVENTS */}
      {past.length > 0 && (
        <section className="py-10 sm:py-12 bg-white border-t border-slate-100" data-testid="past-events">
          <div className="container-site">
            <Reveal className="mb-8 max-w-2xl">
              <p className="label-eyebrow mb-3">Past Events</p>
              <h2 className="font-display font-black text-xl sm:text-2xl lg:text-3xl tracking-tight text-ink" data-testid="past-events-heading">
                Previously at <span className="text-pool">Poolside.</span>
              </h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="past-events-grid">
              {past.slice(0, 3).map((ev, i) => (
                <PastEventCard key={ev.id} ev={ev} index={i} />
              ))}
            </div>
            {past.length > 3 && (
              <Reveal className="mt-8 text-center">
                <Link to="/spotlight" className="text-[11px] tracking-[0.2em] uppercase font-bold text-slate-400 hover:text-pool transition-colors" data-testid="past-events-view-all">
                  View All Past Events <ArrowRight className="inline h-3.5 w-3.5" />
                </Link>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* 3 — BOOK YOUR EVENT */}
      <section className="py-16 sm:py-20 bg-white" data-testid="book-your-event" id="book-your-event">
        <div className="container-site">
          <Reveal className="water-panel text-white rounded-2xl p-10 sm:p-16 noise-overlay">
            <img src="/assets/water.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20 pointer-events-none" />
            <div className="relative">
              <p className="label-eyebrow mb-5">Book Your Event</p>
              <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tight leading-[1.02] max-w-2xl">
                Your celebration deserves the <span className="font-script font-semibold text-pool-light text-4xl sm:text-6xl">Poolside touch.</span>
              </h2>
              <p className="mt-6 text-white/75 max-w-xl leading-relaxed">
                Poolside Cooking Café is available for private celebrations, brunches, dinners and special
                gatherings. Tell us about your event and our team will take it from there.
              </p>
              <p
                className="mt-5 inline-block bg-pool/20 border border-pool/40 text-pool-light text-xs sm:text-sm font-bold tracking-wide px-5 py-2.5 rounded-full"
                data-testid="events-booking-note"
              >
                Event booking available Saturdays &amp; Sundays after 4 PM
              </p>
              <Link
                to="/contact?type=Event%20Booking"
                data-testid="events-book-cta"
                className="mt-10 inline-flex items-center gap-3 bg-pool hover:bg-pool-dark text-white font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors"
              >
                Book Your Event <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4 — TAKE A LOOK INSIDE THE CAFÉ */}
      <section className="py-16 sm:py-20 bg-cream" data-testid="venue-gallery">
        <div className="container-site">
          <Reveal className="mb-12">
            <p className="label-eyebrow mb-4">Poolside Cooking Café</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-[2.6rem] tracking-tight text-ink" data-testid="venue-heading">
              Take a Look <span className="text-pool">Inside.</span>
            </h2>
            <p className="mt-4 text-slate-600 max-w-xl text-sm sm:text-base leading-relaxed">
              A peek inside the Poolside Cooking Café — where the show tapes and celebrations come to life.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="venue-gallery-grid">
            {VENUE_PHOTOS.map((p, i) => (
              <Reveal key={p.src} delay={i * 0.06} className={i === 0 ? "col-span-2 row-span-2" : ""}>
                <div className={`relative overflow-hidden rounded-xl shadow-md h-full ${i === 0 ? "aspect-square lg:aspect-auto" : "aspect-square"}`} data-testid={`venue-photo-${i}`}>
                  <img src={p.src} alt={p.alt} loading="lazy" className="img-zoom absolute inset-0 h-full w-full object-cover" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
